import { Box, Button, Dialog, Grid, LinearProgress } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ListRequestOfBaseFilter,
  ListResponseOfSkuMajorTopic,
  SkuMajorTopic,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import MajorTopicBox from "./SkuMajorTopicBox";
import SkuMajorTopicForm from "./SkuMajorTopicForm";
import { useStrictQuery } from "hooks/useStrictQuery";
import { withErrorBoundary } from "hocs/withErrorBoundary";

const useStyles = makeStyles((theme: Theme) =>
  ({
    root: {
      flexGrow: 1,
    },
    paper: {
      width: "100%",
      height: "20vh",
      overflow: "auto",
    },
  })
);

const SkuMajorTopics = ({ sku, ...props }: SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const styles = useStyles(useTheme());
  const [createOpen, createOpenActions] = useBoolean(false);

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuMajorTopic>(
    ["skuMajorTopics", sku.id!],
    () =>
      skuService.getSkuMajorTopics(
        sku.id!,
        ListRequestOfBaseFilter.fromJS({
          sortField: "createdOn",
          sortDescending: true,
        })
      )
  );

  const skuMajorTopics = useMemo(() => (data?.data ?? []) as SkuMajorTopic[], [
    data?.data,
  ]);

  const handleDelete = useCallback(
    (id: string) => {
      if (id)
        return registerPromise(skuService.deleteSkuMajorTopic(id))
          .then(() => {
            queryClient.invalidateQueries(["skuMajorTopics", sku.id!]);

            setSnackbarMessage(
              new SnackbarMessageProps(
                t({ id: "skuMajorTopicDeleted" }),
                Severity.Warning
              )
            );
          })
          .catch((err: any) => {
            console.log(err);
          });
    },
    [queryClient, registerPromise, sku.id, skuService, t]
  );

  return (
    <>
      <Box sx={styles.root}>
        {isFetching && <LinearProgress color="secondary" />}
        <Box mb={1}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box textAlign="right">
                <Button
                  variant="outlined"
                  type="submit"
                  color="primary"
                  disabled={isFetching}
                  onClick={createOpenActions.setTrue}
                  data-cy="addMajorTopicButton"
                >
                  <T id="skuMajorTopicCreateButtonLabel" />
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        {skuMajorTopics.map((skuMajorTopic, i) => {
          return (
            <Grid key={skuMajorTopic?.id} container spacing={4}>
              <Grid item xs={12}>
                <MajorTopicBox
                  skuMajorTopic={skuMajorTopic}
                  deleteMajorTopic={handleDelete}
                  sku={sku}
                  index={i}
                  {...props}
                ></MajorTopicBox>
              </Grid>
            </Grid>
          );
        })}
        <SnackbarMessage
          snackbarMessage={snackbarMessage}
          setSnackbarMessage={setSnackbarMessage}
        />
      </Box>
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuMajorTopicForm
          onClose={createOpenActions.setFalse}
          sku={sku}
          {...props}
        />
      </Dialog>
    </>
  );
};

export default withErrorBoundary(SkuMajorTopics);
